import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Loader2, Trash2, FileText, Play, RotateCcw, X, Ban, CheckCircle2, Radio, Clock } from "lucide-react";
import { api, faNum, faDigits } from "../lib/api";
import ProviderScriptDialog from "./ProviderScriptDialog";

const MASK = "••••••";

function hostOf(url) {
  try { return new URL(url).host; } catch { return url || "-"; }
}

function StatusBadge({ item }) {
  if (item.auto_disabled) return <span className="px-2 py-0.5 rounded-full text-xs border bg-rose-50 text-rose-700 border-rose-200">قطع خودکار</span>;
  if (!item.enabled) return <span className="px-2 py-0.5 rounded-full text-xs border bg-slate-100 text-slate-600 border-slate-200">غیرفعال</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs border bg-emerald-50 text-emerald-700 border-emerald-200">فعال</span>;
}

export default function ProvidersPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spec, setSpec] = useState({ open: false, mode: "add", item: null });
  const [testFor, setTestFor] = useState(null);
  const [testInput, setTestInput] = useState({ national_code: "", mobile: "" });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/providers");
      setItems(data.data.items || []);
    } catch {
      toast.error("خطا در دریافت پروایدرها");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // برای تغییر وضعیت و اولویت از PUT عمومی استفاده می‌شود
  const payloadOf = (item, overrides = {}) => ({
    name: item.name, enabled: item.enabled, priority: item.priority, timeout_ms: item.timeout_ms,
    method: item.method, url: item.url, headers: item.headers || {}, body_template: item.body_template || "",
    match_path: item.match_path || "", success_path: item.success_path || "",
    true_values: item.true_values || "", false_values: item.false_values || "",
    sample_national_code: item.sample_national_code || "", sample_mobile: item.sample_mobile || "",
    adapter: item.adapter || "",
    ...overrides,
  });

  const toggle = async (item) => {
    try {
      await api.put(`/admin/providers/${item.id}`, payloadOf(item, { enabled: !item.enabled }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در تغییر وضعیت");
    }
  };

  const move = async (item, dir) => {
    const sorted = [...items].sort((a, b) => a.priority - b.priority);
    const idx = sorted.findIndex((x) => x.id === item.id);
    const other = sorted[idx + dir];
    if (!other) return;
    try {
      await api.put(`/admin/providers/${item.id}`, payloadOf(item, { priority: other.priority }));
      await api.put(`/admin/providers/${other.id}`, payloadOf(other, { priority: item.priority }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در تغییر اولویت");
    }
  };

  const reset = async (item) => {
    try {
      await api.post(`/admin/providers/${item.id}/reset`);
      toast.success("پروایدر دوباره فعال شد");
      load();
    } catch {
      toast.error("خطا در بازنشانی");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`حذف پروایدر «${item.name}»؟`)) return;
    try {
      await api.delete(`/admin/providers/${item.id}`);
      toast.success("حذف شد");
      load();
    } catch {
      toast.error("خطا در حذف");
    }
  };

  const openAdd = () => setSpec({ open: true, mode: "add", item: null });
  const openEdit = (item) => setSpec({ open: true, mode: "edit", item });
  const closeSpec = () => setSpec((s) => ({ ...s, open: false }));

  const openTest = (item) => {
    setTestFor(item);
    setTestResult(null);
    setTestInput({ national_code: item.sample_national_code || "", mobile: item.sample_mobile || "" });
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/admin/providers/${testFor.id}/test`, testInput);
      setTestResult(data.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "خطا در تست");
    }
    setTesting(false);
  };

  const sorted = [...items].sort((a, b) => a.priority - b.priority);

  return (
    <div className="space-y-4" data-testid="providers-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-slate-800">پروایدرهای شاهکار</h3>
          <p className="text-xs text-slate-500 mt-1">
            به ترتیب اولویت (عدد کمتر، اولویت بالاتر) امتحان می‌شوند. اگر پروایدری خطا بدهد یا در تایم‌اوت خود جواب ندهد، پروایدر بعدی پرسیده می‌شود.
          </p>
        </div>
        <button onClick={openAdd} data-testid="provider-add"
          className="px-4 py-2 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm flex items-center gap-2">
          <Plus size={16} /> افزودن پروایدر
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
      ) : sorted.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          هنوز پروایدری تعریف نشده است. با دکمه‌ی «افزودن پروایدر» و انتخاب فایل اسکریپت شروع کنید.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-right text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="py-3 px-3">اولویت</th>
                <th className="py-3 px-3">نام</th>
                <th className="py-3 px-3">آدرس</th>
                <th className="py-3 px-3">وضعیت</th>
                <th className="py-3 px-3">تایم‌اوت</th>
                <th className="py-3 px-3">۲۴ ساعت گذشته</th>
                <th className="py-3 px-3">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((item, i) => {
                const s = item.stats_24h || {};
                const rate = s.calls ? Math.round((s.answered / s.calls) * 100) : null;
                return (
                  <tr key={item.id} className="border-t border-slate-100 align-top">
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <span className="tabular-nums font-semibold w-6 text-center">{faNum(item.priority)}</span>
                        <button onClick={() => move(item, -1)} disabled={i === 0}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">▲</button>
                        <button onClick={() => move(item, 1)} disabled={i === sorted.length - 1}
                          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">▼</button>
                      </div>
                    </td>
                    <td className="py-3 px-3 font-medium text-slate-800">{item.name}</td>
                    <td className="py-3 px-3 text-xs text-slate-500 font-mono" dir="ltr">{item.adapter ? `adapter: ${item.adapter}` : hostOf(item.url)}</td>
                    <td className="py-3 px-3">
                      <StatusBadge item={item} />
                      {item.last_error && (
                        <div className="text-[11px] text-rose-600 mt-1 max-w-[220px] truncate" title={item.last_error}>
                          {item.last_error}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-3 tabular-nums text-xs">{faNum(item.timeout_ms)} ms</td>
                    <td className="py-3 px-3 text-xs">
                      {s.calls ? (
                        <div className="space-y-0.5">
                          <div>پاسخ‌داده: <b className="tabular-nums">{faNum(s.answered)}</b> از <span className="tabular-nums">{faNum(s.calls)}</span>
                            {rate !== null && <span className="text-slate-500"> ({faDigits(rate)}٪)</span>}</div>
                          <div className="text-slate-500">تایم‌اوت {faNum(s.timeouts)} · خطا {faNum(s.errors)} · میانگین {faNum(s.avg_ms)} ms</div>
                        </div>
                      ) : <span className="text-slate-400">بدون استعلام</span>}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1 flex-wrap">
                        <button onClick={() => openTest(item)} title="تست" className="p-1.5 rounded-lg hover:bg-teal-50 text-teal-700"><Play size={15} /></button>
                        <button onClick={() => openEdit(item)} title="ویرایش" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-700"><FileText size={15} /></button>
                        <button onClick={() => toggle(item)} title={item.enabled ? "غیرفعال کردن" : "فعال کردن"}
                          className={`p-1.5 rounded-lg ${item.enabled ? "hover:bg-amber-50 text-amber-700" : "hover:bg-emerald-50 text-emerald-700"}`}>
                          {item.enabled ? <Ban size={15} /> : <CheckCircle2 size={15} />}
                        </button>
                        {(item.auto_disabled || item.consecutive_failures > 0) && (
                          <button onClick={() => reset(item)} title="بازنشانی خطاها" className="p-1.5 rounded-lg hover:bg-sky-50 text-sky-700"><RotateCcw size={15} /></button>
                        )}
                        <button onClick={() => remove(item)} title="حذف" className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProviderScriptDialog
        open={spec.open}
        mode={spec.mode}
        item={spec.item}
        onClose={closeSpec}
        onSaved={load}
      />

      {/* ---------- تست ---------- */}
      <AnimatePresence>
        {testFor && (
          <motion.div key="test" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={() => setTestFor(null)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">تست «{testFor.name}»</h3>
                <button onClick={() => setTestFor(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="کدملی" value={testInput.national_code} onChange={(v) => setTestInput((t) => ({ ...t, national_code: v }))} dir="ltr" />
                <Input label="موبایل" value={testInput.mobile} onChange={(v) => setTestInput((t) => ({ ...t, mobile: v }))} dir="ltr" />
              </div>
              <button onClick={runTest} disabled={testing}
                className="w-full py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                {testing ? <Loader2 size={15} className="animate-spin" /> : <Radio size={15} />} اجرای تست
              </button>
              {testResult && (
                <div className="rounded-xl border border-slate-200 p-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${
                      testResult.kind === "matched" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : testResult.kind === "not_matched" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                      {testResult.kind === "matched" ? "تطابق" : testResult.kind === "not_matched" ? "عدم تطابق" : testResult.kind === "timeout" ? "تایم‌اوت" : "خطا"}
                    </span>
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {faNum(testResult.latency_ms)} ms · HTTP {faNum(testResult.http_status)}</span>
                  </div>
                  {testResult.error && <div className="text-xs text-rose-600">{testResult.error}</div>}
                  {testResult.response_snippet && (
                    <pre dir="ltr" className="text-[11px] bg-slate-50 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap">{testResult.response_snippet}</pre>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", dir, placeholder }) {
  return (
    <div>
      <label className="text-xs text-slate-600 mb-1 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} dir={dir} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-teal-500 outline-none text-sm" />
    </div>
  );
}

export { MASK };
